// src/pages/VehicleEditor.js
import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, deleteDoc, collection, getDocs, query, where, orderBy, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchIncomingShares, fetchSharedItems } from '../utils/sharing';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { IMAGE_PATHS } from '../config';
import { MandatoryParameters, CarTemplates } from '../components/VehicleEditor/DefaultParameters';
import { DefaultHelpTips } from '../components/VehicleEditor/DefaultHelpTips';
import ParameterEditor from '../components/VehicleEditor/ParameterEditor';
import DrivetrainParameterEditor from '../components/VehicleEditor/DrivetrainParameterEditor';
import CompensationEditor from '../components/VehicleEditor/CompensationEditor';
import { updateLastModified } from '../utils/backup';
import { compressImage } from '../utils/imageCompression';
import '../styles.css';

const convertToGroups = (params) => {
  if (Array.isArray(params)) {
    return [{ title: 'Default', params }];
  }
  return Object.entries(params).map(([title, params]) => ({ title, params }));
};

const populateDefaultHelp = (tabs) => {
  tabs.forEach(tab => {
    tab.groups.forEach(group => {
      group.params.forEach(param => {
        if (!param.help && DefaultHelpTips[param.name]) {
          param.help = DefaultHelpTips[param.name];
        }
      });
    });
  });
};

const defaultTabs = [
  { name: 'Info', groups: [{ title: 'General', params: [{ name: 'Vehicle Name', type: 'text', value: '' }, { name: 'Image', type: 'upload', value: '' }, { name: 'Vehicle Notes', type: 'textarea', value: '' }] }] },
  { name: 'Suspension', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Suspension) },
  { name: 'Tires', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Tires) },
  { name: 'Brakes', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Brakes) },
  { name: 'Aero', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Aero) },
  { name: 'Drivetrain', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Drivetrain) },
  { name: 'Engine', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Engine) },
  { name: 'Electronics', groups: convertToGroups(CarTemplates['Club Track Day'].Metric.Electronics) },
];

function VehicleEditor({ subscriptionStatus }) {
  const { id } = useParams();
  const [tabs, setTabs] = useState(defaultTabs);
  const allParams = tabs.filter(tab => tab.name !== 'Compensations').flatMap(tab => tab.groups.flatMap(group => group.params));
  const [currentTab, setCurrentTab] = useState(0);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState(false);
  const [pendingDeleteGroupIdx, setPendingDeleteGroupIdx] = useState(null);
  const [showPopup, setShowPopup] = useState(id === 'new');
  const [showSelectVehicle, setShowSelectVehicle] = useState(false);
  const [showUnitsDialog, setShowUnitsDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('Club Track Day');
  const [units, setUnits] = useState('Metric');
  const [vehicles, setVehicles] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [showCompensationsDialog, setShowCompensationsDialog] = useState(false);
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  // For shared vehicles, read/write from the owner's account.
  const ownerUid = location.state?.ownerUid || null;
  const dataUid = ownerUid || (user ? user.uid : null);

  useEffect(() => {
    document.documentElement.style.setProperty('--tires-image', `url(${IMAGE_PATHS.Tires})`);
    document.documentElement.style.setProperty('--suspension-image', `url(${IMAGE_PATHS.Suspension})`);
    document.documentElement.style.setProperty('--brakes-image', `url(${IMAGE_PATHS.Brakes})`);
    document.documentElement.style.setProperty('--aero-image', `url(${IMAGE_PATHS.Aero})`);
    document.documentElement.style.setProperty('--drivetrain-image', `url(${IMAGE_PATHS.Drivetrain})`);
    document.documentElement.style.setProperty('--engine-image', `url(${IMAGE_PATHS.Engine})`);
    document.documentElement.style.setProperty('--electronics-image', `url(${IMAGE_PATHS.Electronics})`);
    document.documentElement.style.setProperty('--info-image', `url(${IMAGE_PATHS.Info})`);
    return () => {
      document.documentElement.style.removeProperty('--tires-image');
      document.documentElement.style.removeProperty('--suspension-image');
      document.documentElement.style.removeProperty('--brakes-image');
      document.documentElement.style.removeProperty('--aero-image');
      document.documentElement.style.removeProperty('--drivetrain-image');
      document.documentElement.style.removeProperty('--engine-image');
      document.documentElement.style.removeProperty('--electronics-image');
      document.documentElement.style.removeProperty('--info-image');
    };
  }, []);

  useEffect(() => {
    if (loading) {
      setSyncStatus('syncing');
      return;
    }
    if (error || !user) {
      console.error('Auth error or no user:', error);
      setSyncStatus('Error: Not logged in');
      navigate('/');
      return;
    }

    const loadVehicle = async () => {
      setSyncStatus('syncing');
      try {
        let loadedTabs = [...defaultTabs];
        if (id !== 'new') {
          const vehicleDoc = await getDoc(doc(db, `users/${dataUid}/vehicles/${id}`));
          if (vehicleDoc.exists()) {
            const loadedVehicle = vehicleDoc.data();
            loadedTabs = loadedVehicle.tabs || defaultTabs;

            // ===========================================
            // MIGRATION CODE - START
            // This code migrates old vehicles with "Chassis" tabs to the new "Suspension" structure
            // Can be safely removed ~6 months after deployment when all vehicles have been migrated
            // ===========================================

            // Migrate old Chassis data to Suspension.Weights for backwards compatibility
            const chassisTab = loadedTabs.find(tab => tab.name === 'Chassis');
            const suspensionTab = loadedTabs.find(tab => tab.name === 'Suspension');

            if (chassisTab && suspensionTab) {
              // Ensure Suspension tab has proper group structure
              const defaultSuspensionGroups = convertToGroups(CarTemplates['Club Track Day'].Metric.Suspension);

              // Ensure all default groups exist in suspension tab
              defaultSuspensionGroups.forEach(defaultGroup => {
                const existingGroup = suspensionTab.groups.find(g => g.title === defaultGroup.title);
                if (!existingGroup) {
                  suspensionTab.groups.push({ ...defaultGroup });
                } else {
                  // Ensure mandatory parameters exist in existing groups
                  defaultGroup.params.forEach(defaultParam => {
                    const existingParam = existingGroup.params.find(p => p.name === defaultParam.name);
                    if (!existingParam) {
                      existingGroup.params.push({ ...defaultParam });
                    }
                  });
                }
              });

              // Move chassis parameters to suspension Weights group
              const weightsGroup = suspensionTab.groups.find(group => group.title === 'Weights');

              if (weightsGroup && chassisTab.groups && chassisTab.groups.length > 0) {
                // Move all chassis parameters to the Weights group
                chassisTab.groups.forEach(chassisGroup => {
                  if (chassisGroup.params) {
                    chassisGroup.params.forEach(param => {
                      // Check if parameter already exists in Weights to avoid duplicates
                      const existingParam = weightsGroup.params.find(p => p.name === param.name);
                      if (!existingParam) {
                        weightsGroup.params.push({ ...param });
                      }
                    });
                  }
                });
              }
              // Remove the old Chassis tab
              loadedTabs = loadedTabs.filter(tab => tab.name !== 'Chassis');

              // Save the migrated vehicle data back to Firestore
              try {
                await updateDoc(doc(db, `users/${dataUid}/vehicles/${id}`), {
                  tabs: loadedTabs,
                  migratedAt: new Date().toISOString()
                });
              } catch (error) {
                console.error('Failed to save migrated vehicle data:', error);
              }
            }

            // ===========================================
            // MIGRATION CODE - END
            // Remove everything between MIGRATION CODE comments when all vehicles are migrated
            // ===========================================

            loadedTabs[0].groups[0].params.find(p => p.name === 'Vehicle Name').value = loadedVehicle.name || '';
            loadedTabs[0].groups[0].params.find(p => p.name === 'Vehicle Notes').value = loadedVehicle.notes || '';
            loadedTabs[0].groups[0].params.find(p => p.name === 'Image').value = loadedVehicle.image || '';
          } else {
            console.warn('Vehicle not found:', id);
          }
        }
        // Ensure Compensations tab exists and has at least one group
        if (!loadedTabs.find(t => t.name === 'Compensations')) {
          loadedTabs.push({ name: 'Compensations', groups: [{ title: 'Default', params: [] }] });
        } else {
          const compTab = loadedTabs.find(t => t.name === 'Compensations');
          if (!compTab.groups || compTab.groups.length === 0) {
            compTab.groups = [{ title: 'Default', params: [] }];
          }
        }
        setTabs(loadedTabs);
        setInitialData(JSON.stringify(loadedTabs));
        setSyncStatus(navigator.onLine ? 'synced' : 'offline');
      } catch (err) {
        console.error('Vehicle load error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    loadVehicle();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);
    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [id, user, loading, error, navigate]);

  useEffect(() => {
    if (loading || error || !user) return;
    const fetchVehicles = async () => {
      try {
        const vehiclesQuery = query(collection(db, `users/${user.uid}/vehicles`), orderBy('timestamp', 'desc'));
        const vehiclesSnap = await getDocs(vehiclesQuery);
        const fetchedVehicles = vehiclesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Include vehicles shared with this user so they can be duplicated too.
        let sharedVehicles = [];
        try {
          const shares = await fetchIncomingShares(user.uid);
          sharedVehicles = (await fetchSharedItems(shares)).vehicles;
        } catch (shareErr) {
          console.error('Error fetching shared vehicles:', shareErr.message);
        }
        setVehicles([...fetchedVehicles, ...sharedVehicles]);
      } catch (err) {
        console.error('Error fetching vehicles:', err.message);
      }
    };
    fetchVehicles();
  }, [user, loading, error]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowPopup(false);
    setShowUnitsDialog(true);
  };

  const handleCreateVehicle = async (units) => {
    setShowUnitsDialog(false);
    const updatedTabs = [...tabs];
    const defaults = CarTemplates[selectedTemplate][units];
    updatedTabs[1].groups = convertToGroups(defaults.Suspension);
    updatedTabs[2].groups = convertToGroups(defaults.Tires);
    updatedTabs[3].groups = convertToGroups(defaults.Brakes);
    updatedTabs[4].groups = convertToGroups(defaults.Aero);
    updatedTabs[5].groups = convertToGroups(defaults.Drivetrain);
    updatedTabs[6].groups = convertToGroups(defaults.Engine);
    updatedTabs[7].groups = convertToGroups(defaults.Electronics);
    populateDefaultHelp(updatedTabs);
    setTabs(updatedTabs);
    setInitialData(JSON.stringify(updatedTabs));
  };

  const handleDuplicateVehicle = (vehicle) => {
    const updatedTabs = [...vehicle.tabs];
    updatedTabs[0].groups[0].params.find(p => p.name === 'Vehicle Name').value = '';
    updatedTabs[0].groups[0].params.find(p => p.name === 'Image').value = '';
    updatedTabs[0].groups[0].params.find(p => p.name === 'Vehicle Notes').value = '';
    setTabs(updatedTabs);
    setInitialData(JSON.stringify(updatedTabs));
    setShowSelectVehicle(false);
  };

  const addGroup = () => {
    if (!newGroupTitle) {
      alert('Group title is required');
      return;
    }
    const updatedTabs = [...tabs];
    updatedTabs[currentTab].groups.push({ title: newGroupTitle, params: [] });
    setTabs(updatedTabs);
    setShowNewGroup(false);
    setNewGroupTitle('');
  };

  const addDamperGraph = () => {
    if (allParams.some(p => p.isDamperGraph)) {
      alert('A Damper Force Graph has already been added to this vehicle');
      return;
    }
    const updatedTabs = [...tabs];
    const groups = updatedTabs[currentTab].groups;
    groups[groups.length - 1].params.push({ name: 'Damper Force Graph', isDamperGraph: true });
    setTabs(updatedTabs);
  };

  const editGroupTitle = (groupIdx, newTitle) => {
    const updatedTabs = [...tabs];
    updatedTabs[currentTab].groups[groupIdx].title = newTitle;
    setTabs(updatedTabs);
  };

  const deleteGroup = (groupIdx) => {
    const groupParams = tabs[currentTab].groups[groupIdx].params;
    if (groupParams.some(param => (MandatoryParameters[tabs[currentTab].name] || []).includes(param.name))) {
      alert('Cannot delete group containing mandatory parameters');
      return;
    }
    setPendingDeleteGroupIdx(groupIdx);
    setShowDeleteGroupConfirm(true);
  };

  const confirmDeleteGroup = () => {
    const updatedTabs = [...tabs];
    updatedTabs[currentTab].groups.splice(pendingDeleteGroupIdx, 1);
    setTabs(updatedTabs);
    setShowDeleteGroupConfirm(false);
    setPendingDeleteGroupIdx(null);
  };

  const moveGroup = (groupIdx, direction) => {
    const updatedTabs = [...tabs];
    const groups = updatedTabs[currentTab].groups;
    if (direction === 'up' && groupIdx > 0) {
      [groups[groupIdx - 1], groups[groupIdx]] = [groups[groupIdx], groups[groupIdx - 1]];
    } else if (direction === 'down' && groupIdx < groups.length - 1) {
      [groups[groupIdx], groups[groupIdx + 1]] = [groups[groupIdx + 1], groups[groupIdx]];
    }
    setTabs(updatedTabs);
  };

  const handleTabClick = (idx) => {
    if (tabs[idx].name === 'Compensations' && subscriptionStatus?.plan === 'enthusiast') {
      setShowCompensationsDialog(true);
    } else {
      setCurrentTab(idx);
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const updatedTabs = [...tabs];
    const sourceGroupIdx = Number(result.source.droppableId);
    const destGroupIdx = Number(result.destination.droppableId);
    const [moved] = updatedTabs[currentTab].groups[sourceGroupIdx].params.splice(result.source.index, 1);
    updatedTabs[currentTab].groups[destGroupIdx].params.splice(result.destination.index, 0, moved);
    setTabs(updatedTabs);
  };

  const saveVehicle = async () => {
    if (!user) {
      setSyncStatus('Error: Not logged in');
      return;
    }
    const vehicleId = id === 'new' ? Date.now().toString() : id;
    const vehicleData = {
      tabs,
      name: tabs[0].groups[0].params.find(p => p.name === 'Vehicle Name')?.value || '',
      image: tabs[0].groups[0].params.find(p => p.name === 'Image')?.value || '',
      notes: tabs[0].groups[0].params.find(p => p.name === 'Vehicle Notes')?.value || '',
      timestamp: Date.now()
    };
    try {
      setSyncStatus('syncing');
      // Merge so data owned by other features (e.g. saved suspension kinematics) is kept.
      await setDoc(doc(db, `users/${dataUid}/vehicles/${vehicleId}`), vehicleData, { merge: true });
      await updateLastModified(user);
      setInitialData(JSON.stringify(tabs));
      setSyncStatus('synced');
      navigate('/');
    } catch (err) {
      console.error('Save vehicle error:', err.message);
      setSyncStatus(`Error: ${err.message}`);
      alert('Failed to save vehicle: ' + err.message);
    }
  };

  const deleteVehicle = async () => {
    if (!user || id === 'new') return;
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (!user || id === 'new') return;
    try {
      setSyncStatus('syncing');
      // Delete image from Storage if exists
      const vehicleDoc = await getDoc(doc(db, `users/${dataUid}/vehicles/${id}`));
      if (vehicleDoc.exists() && vehicleDoc.data().image) {
        const imageRef = ref(storage, vehicleDoc.data().image);
        try {
          await deleteObject(imageRef);
        } catch (err) {
          console.error('Error deleting image:', err.message);
        }
      }
      // Delete vehicle
      await deleteDoc(doc(db, `users/${dataUid}/vehicles/${id}`));
      // Delete associated sessions
      const sessionsQuery = query(
        collection(db, `users/${dataUid}/sessions`),
        where('vehicleId', '==', Number(id))
      );
      const sessionsSnap = await getDocs(sessionsQuery);
      const deletePromises = sessionsSnap.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      setSyncStatus('synced');
      setShowConfirmDelete(false);
      navigate('/');
    } catch (err) {
      console.error('Delete vehicle error:', err.message);
      setSyncStatus(`Error: ${err.message}`);
      alert('Failed to delete vehicle: ' + err.message);
    }
  };

  const handleClose = () => {
    if (initialData && JSON.stringify(tabs) !== initialData) {
      setShowDiscardConfirm(true);
    } else {
      navigate('/');
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    navigate('/');
  };

  const handleInfoChange = async (field, value) => {
    const updatedTabs = [...tabs];
    const infoParams = updatedTabs[0].groups[0].params;
    if (field === 'Vehicle Name') {
      infoParams.find(p => p.name === 'Vehicle Name').value = value;
    } else if (field === 'Image' && value.target.files[0]) {
      const file = value.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit');
        return;
      }
      const vehicleId = id === 'new' ? Date.now().toString() : id;
      const oldImage = infoParams.find(p => p.name === 'Image').value;
      const imageRef = ref(storage, `vehicles/${user.uid}/${vehicleId}-${Date.now()}.png`);
      try {
        setSyncStatus('Compressing image...');
        const compressedFile = await compressImage(file, 'vehicle');
        setSyncStatus('Uploading image...');
        await uploadBytes(imageRef, compressedFile);
        const imageUrl = await getDownloadURL(imageRef);
        infoParams.find(p => p.name ===  'Image').value = imageUrl + '?t=' + Date.now();
        // Cache the new image
        caches.open('hot-setup-cache-v5').then(cache => cache.add(imageUrl));
        // Delete old image if exists
        if (oldImage) {
          const oldPath = oldImage.split('/o/')[1].split('?')[0];
          const oldRef = ref(storage, decodeURIComponent(oldPath));
          await deleteObject(oldRef);
        }
        setSyncStatus('synced');
      } catch (err) {
        console.error('Error uploading image:', err.message);
        setSyncStatus(`Error: ${err.message}`);
        alert('Failed to upload image: ' + err.message);
      }
    } else if (field === 'Vehicle Notes') {
      infoParams.find(p => p.name === 'Vehicle Notes').value = value;
    }
    setTabs(updatedTabs);
  };

  return (
    <div className="vehicle-editor-container">
      <Header syncStatus={syncStatus} />
      <div className="page-title">
        <h1>{tabs[0].groups[0].params.find(p => p.name === 'Vehicle Name')?.value || 'Vehicle Editor'}</h1>
        <button onClick={handleClose} className="close-button">✕</button>
      </div>
      <div className="tab-container">
        <div className="tab-list">
          {tabs.map((tab, idx) => (
            <p
              key={idx}
              onClick={() => handleTabClick(idx)}
              className={`tab-item ${currentTab === idx ? 'active' : ''} ${tab.name === 'Compensations' && subscriptionStatus?.plan === 'enthusiast' ? 'disabled' : ''}`}
              data-tab-name={tab.name}
            >
              {tab.name}
              {tab.name === 'Compensations' && subscriptionStatus?.plan === 'enthusiast' && <span className="pro-tab-overlay">PRO</span>}
            </p>
          ))}
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="tab-content">
            {tabs[currentTab].name === 'Info' ? (
              <div className="tab-group">
                <h3 className="tab-group-title">General</h3>
                <div className="tab-param">
                  <span>Vehicle Name</span>
                  <input
                    value={tabs[0].groups[0].params.find(p => p.name === 'Vehicle Name')?.value || ''}
                    onChange={e => handleInfoChange('Vehicle Name', e.target.value)}
                    className="info-input"
                  />
                </div>
                <div className="tab-param">
                  <span>Image</span>
                  <input type="file" onChange={e => handleInfoChange('Image', e)} className="info-input" accept="image/*" />
                </div>
                <div className="image-preview">
                  {tabs[0].groups[0].params.find(p => p.name === 'Image')?.value && (
                    <img src={tabs[0].groups[0].params.find(p => p.name === 'Image').value} alt="Vehicle" style={{ maxWidth: '90%', margin: '20px', borderRadius: '20px' }} />
                  )}
                </div>
                <div className="tab-param">
                  <span>Vehicle Notes</span>
                  <textarea
                    value={tabs[0].groups[0].params.find(p => p.name === 'Vehicle Notes')?.value || ''}
                    onChange={e => handleInfoChange('Vehicle Notes', e.target.value)}
                    className="info-textarea"
                  />
                </div>
              </div>
            ) : (
              tabs[currentTab].groups.map((group, gIdx) => (
                <div key={gIdx} className="tab-group">
                  <div className="tab-group-title-container">
                    <input
                      value={group.title}
                      onChange={e => editGroupTitle(gIdx, e.target.value)}
                      className="tab-group-title"
                      placeholder="Group Title"
                    />
                    <div className="group-actions">
                      <button onClick={() => moveGroup(gIdx, 'up')} className="btn btn-group-action" disabled={gIdx === 0}>↑</button>
                      <button onClick={() => moveGroup(gIdx, 'down')} className="btn btn-group-action" disabled={gIdx === tabs[currentTab].groups.length - 1}>↓</button>
                      <button onClick={() => deleteGroup(gIdx)} className="btn btn-group-action delete">−</button>
                    </div>
                  </div>
                  <Droppable droppableId={gIdx.toString()}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {group.params.map((param, pIdx) => (
                          <Draggable key={param.name} draggableId={param.name} index={pIdx}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="tab-param"
                              >
                                <b className="drag-handle">☰</b>
                                <span className="param-name">{param.name}</span>
                                <span className="param-details">{tabs[currentTab].name === 'Compensations' ? `(${param.value.adjustedBy})` : (param.unit && param.unit !== 'text' ? `(${param.unit}, ${param.min} to ${param.max}, ${param.step})` : '')}</span>
                                {tabs[currentTab].name === 'Drivetrain' && MandatoryParameters.Drivetrain.some(p => p.name === param.name) ? (
                                  <DrivetrainParameterEditor
                                    group={group}
                                    groupIdx={gIdx}
                                    setTabs={setTabs}
                                  />
                                ) : tabs[currentTab].name === 'Compensations' ? (
                                  <CompensationEditor
                                    param={param}
                                    groupIdx={gIdx}
                                    paramIdx={pIdx}
                                    setTabs={setTabs}
                                    currentTab={currentTab}
                                    allParams={allParams}
                                  />
                                ) : (
                                  <ParameterEditor
                                    param={param}
                                    groupIdx={gIdx}
                                    paramIdx={pIdx}
                                    setTabs={setTabs}
                                    nonRemovableNames={MandatoryParameters[tabs[currentTab].name] || []}
                                    currentTab={currentTab}
                                  />
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              ))
            )}
            {tabs[currentTab].name !== 'Info' && (
              <div className="tab-actions">
                <button onClick={() => setShowNewGroup(true)} className="btn btn-secondary">Add Group</button>
                {tabs[currentTab].name === 'Compensations' ? (
                  <CompensationEditor
                    isNew={true}
                    setTabs={setTabs}
                    currentTab={currentTab}
                    allParams={allParams}
                    key={`compensation-editor-${currentTab}`}
                  />
                ) : (
                  <>
                    <ParameterEditor
                      isNew={true}
                      setTabs={setTabs}
                      currentTab={currentTab}
                      key={`param-editor-${currentTab}`}
                    />
                    <button onClick={addDamperGraph} className="btn btn-save">Add Damper Graph (+)</button>
                  </>
                )}
              </div>
            )}
          </div>
        </DragDropContext>
      </div>
      {showNewGroup && (
        <div className="popup">
          <input
            value={newGroupTitle}
            onChange={e => setNewGroupTitle(e.target.value)}
            placeholder="Group Title"
            className="event-editor-input"
          />
          <button onClick={addGroup} className="btn btn-save">Add</button>
          <button onClick={() => setShowNewGroup(false)} className="btn btn-secondary">Cancel</button>
        </div>
      )}
      {showPopup && (
        <div className="overlay-card">
          <div className="card-container">
            <div className="page-title" style={{ marginBottom: '20px' }}>
              <h1>Create Vehicle</h1>
              <button onClick={() => navigate('/')} className="close-button">✕</button>
            </div>
            <button onClick={() => handleTemplateSelect('Club Track Day')} className="btn btn-save popup-btn" style={{ width: '100%', margin: '5px 0' }}>Club Track Day</button>
            <button onClick={() => handleTemplateSelect('GT4 / TCR')} className="btn btn-save popup-btn" style={{ width: '100%', margin: '5px 0' }}>GT4 / TCR</button>
            <button onClick={() => handleTemplateSelect('GT3 Racecar')} className="btn btn-save popup-btn" style={{ width: '100%', margin: '5px 0' }}>GT3 Racecar</button>
            <button onClick={() => handleTemplateSelect('Open Wheel')} className="btn btn-save popup-btn" style={{ width: '100%', margin: '5px 0' }}>Open Wheel</button>
            <button onClick={() => handleTemplateSelect('Time Attack')} className="btn btn-save popup-btn" style={{ width: '100%', margin: '5px 0' }}>Time Attack</button>
            <button onClick={() => { setShowPopup(false); setShowSelectVehicle(true); }} className="btn btn-secondary popup-btn" style={{ width: '100%', margin: '5px 0' }}>Duplicate Existing Vehicle</button>
          </div>
        </div>
      )}
      {showUnitsDialog && (
        <div className="overlay-card">
          <div className="card-container">
            <div className="page-title" style={{ marginBottom: '20px' }}>
              <h1>New {selectedTemplate} Vehicle</h1>
              <button onClick={() => { setShowUnitsDialog(false); setShowPopup(true); }} className="close-button">✕</button>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => handleCreateVehicle('Metric')} className="btn btn-save popup-btn" style={{ flex: 1 }}>Metric</button>
              <button onClick={() => handleCreateVehicle('Imperial')} className="btn btn-save popup-btn" style={{ flex: 1 }}>Imperial</button>
            </div>
          </div>
        </div>
      )}
      {showSelectVehicle && (
        <div className="select-other-overlay">
          <div className="select-other-dialog">
            <div className="page-title" style={{ marginBottom: '20px' }}>
              <h1>Select Vehicle to Duplicate</h1>
              <button onClick={() => navigate('/')} className="close-button">✕</button>
            </div>
            <div className="vehicles-list">
              {vehicles.map(vehicle => (
                <div
                  key={vehicle.id}
                  onClick={() => handleDuplicateVehicle(vehicle)}
                  className="vehicle-card"
                  style={{
                    backgroundImage: `url(${vehicle.image || '/images/default-vehicle.jpg'})`,
                    backgroundSize: 'cover',
                  }}
                >
                  <div className="vehicle-card-overlay">
                    <div className="vehicle-name">{vehicle.name || 'Unnamed Vehicle'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="session-buttons">
        <button onClick={saveVehicle} className="btn btn-save">Save Vehicle</button>
        {id !== 'new' && (
          <>
            <button onClick={() => navigate(`/setup-history/${id}`)} className="btn btn-secondary">Setup History</button>
            {!ownerUid && <button onClick={deleteVehicle} className="btn btn-danger">Delete Vehicle</button>}
          </>
        )}
      </div>
      {showDiscardConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to discard unsaved changes?"
          onConfirm={confirmDiscard}
          onCancel={() => {
            setShowDiscardConfirm(false);
          }}
        />
      )}
      {showConfirmDelete && (
        <ConfirmationDialog
          message="Are you sure you want to delete this vehicle and all its sessions?"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowConfirmDelete(false);
          }}
        />
      )}
      {showDeleteGroupConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to delete this group?"
          onConfirm={confirmDeleteGroup}
          onCancel={() => {
            setShowDeleteGroupConfirm(false);
            setPendingDeleteGroupIdx(null);
          }}
        />
      )}
      {showCompensationsDialog && (
        <ConfirmationDialog
          title="Pro Feature"
          message="Compensation calculations are available with the Pro plan"
          onConfirm={handleUpgrade}
          onCancel={() => setShowCompensationsDialog(false)}
          confirmText="Upgrade to Pro"
          cancelText="Ok"
        />
      )}
    </div>
  );
}

export default VehicleEditor;